import React, { useState, useEffect, useMemo } from 'react'
import HostNav from '../components/hostComponents/HostNav'
import { useNavigate } from 'react-router-dom'
import { getAllOrders } from '../store/thunks/hostActions'
import { useDispatch, useSelector } from 'react-redux'
import { CircularProgress } from '@mui/material'
import { Link } from 'react-router-dom'
import { isUpcomingOrder } from '../utils/orderStatus'

const normalizeStatus = (status) => String(status || '').trim().toLowerCase()

const isStatus = (status, target) => normalizeStatus(status) === normalizeStatus(target)
const isFinalizedOrder = (order) => !isStatus(order?.status, 'created')
const isEntryInstructionsEditable = (order) =>
  Boolean(order?._id) &&
  !isStatus(order?.status, 'completed') &&
  !isStatus(order?.status, 'cancelled') &&
  !isStatus(order?.status, 'canceled')

function normalizeAddressInput(raw) {
  if (raw == null) return null
  if (typeof raw === 'object' && !Array.isArray(raw)) {
    if (Object.keys(raw).length === 0) return null
    return raw
  }
  if (typeof raw === 'string') {
    const trimmed = raw.trim()
    if (!trimmed || trimmed === '{}' || trimmed === '[]') return null
    try {
      const parsed = JSON.parse(trimmed)
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) return parsed
    } catch (_) {
      /* plain string */
    }
    return { _plain: trimmed }
  }
  return null
}

function structuredAddressLines(obj) {
  if (!obj || obj._plain) return null
  const { propertyManager, line1, line2, city, state, zipCode, entryInstructions } = obj
  const lines = []
  if (propertyManager != null && String(propertyManager).trim()) lines.push(String(propertyManager).trim())
  const streetParts = [line1, line2]
    .map((x) => (x != null ? String(x).trim() : ''))
    .filter(Boolean)
  if (streetParts.length) lines.push(streetParts.join(', '))
  const cityPart = city != null ? String(city).trim() : ''
  const statePart = state != null ? String(state).trim() : ''
  const zipPart = zipCode != null ? String(zipCode).trim() : ''
  let locality = ''
  if (cityPart || statePart) {
    locality = [cityPart, statePart].filter(Boolean).join(', ')
    if (zipPart) locality = `${locality} ${zipPart}`.trim()
  } else if (zipPart) {
    locality = zipPart
  }
  if (locality) lines.push(locality)
  if (entryInstructions != null && String(entryInstructions).trim()) {
    lines.push(String(entryInstructions).trim())
  }
  return lines.length ? lines : null
}

function deliveryAddressToDisplay(raw) {
  const norm = normalizeAddressInput(raw)
  if (!norm) return null
  if (norm._plain) return { kind: 'text', value: norm._plain }
  const lines = structuredAddressLines(norm)
  if (lines?.length) return { kind: 'lines', lines }
  const parts = [norm.line1, norm.line2, norm.city, norm.state, norm.zipCode].filter((x) => x != null && String(x).trim())
  if (parts.length) return { kind: 'text', value: parts.map((x) => String(x).trim()).join(', ') }
  return null
}

function getOrderDeliveryDisplay(order) {
  const sources = [order.deliveryAddress, order.userInputAddress, order.property?.address]
  for (const raw of sources) {
    const disp = deliveryAddressToDisplay(raw)
    if (disp) return disp
  }
  return { kind: 'text', value: 'N/A' }
}

function formatExpectedDeliveryPacific(dateString) {
  if (!dateString) return 'N/A'
  const date = new Date(dateString)
  if (Number.isNaN(date.getTime())) return 'N/A'
  return date.toLocaleString('en-US', {
    timeZone: 'America/Los_Angeles',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
    timeZoneName: 'short',
  })
}

const ARRIVAL_PERIOD_OPTIONS = [
  { value: 'ytd', label: 'YTD' },
  { value: 'mtd', label: 'MTD' },
  { value: 'allTime', label: 'All Time' },
  { value: 'custom', label: 'Custom' },
]

function startOfLocalDay(date) {
  const d = new Date(date)
  d.setHours(0, 0, 0, 0)
  return d
}

function endOfLocalDay(date) {
  const d = new Date(date)
  d.setHours(23, 59, 59, 999)
  return d
}

function parseInStockDate(value) {
  if (!value) return null
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return null
  return date
}

function getArrivalDateRange(period, customFrom, customTo) {
  const now = new Date()
  if (period === 'ytd') {
    return {
      from: startOfLocalDay(new Date(now.getFullYear(), 0, 1)),
      to: endOfLocalDay(now),
    }
  }
  if (period === 'mtd') {
    return {
      from: startOfLocalDay(new Date(now.getFullYear(), now.getMonth(), 1)),
      to: endOfLocalDay(now),
    }
  }
  if (period === 'custom') {
    return {
      from: customFrom ? startOfLocalDay(new Date(`${customFrom}T00:00:00`)) : null,
      to: customTo ? endOfLocalDay(new Date(`${customTo}T00:00:00`)) : null,
    }
  }
  return { from: null, to: null }
}

function orderMatchesArrivalPeriod(order, period, customFrom, customTo) {
  if (period === 'allTime') return true
  const arrival = parseInStockDate(order?.inStockDate)
  if (!arrival) return false
  const { from, to } = getArrivalDateRange(period, customFrom, customTo)
  if (from && arrival < from) return false
  if (to && arrival > to) return false
  if (period === 'custom' && !from && !to) return true
  return true
}

function formatAddressPlain(address) {
  if (!address) return ''
  if (typeof address === 'string') {
    const trimmed = address.trim()
    if (!trimmed || trimmed === '{}' || trimmed === '[]') return ''
    const parsed = normalizeAddressInput(trimmed)
    if (parsed && !parsed._plain) {
      const lines = structuredAddressLines(parsed)
      if (lines?.length) return lines.join(', ')
      return [parsed.line1, parsed.line2, parsed.city, parsed.state, parsed.zipCode]
        .filter((x) => x != null && String(x).trim())
        .map((x) => String(x).trim())
        .join(', ')
    }
    return trimmed
  }
  return [address.line1, address.line2, address.city, address.state, address.zipCode]
    .filter((x) => x != null && String(x).trim())
    .map((x) => String(x).trim())
    .join(', ')
}

function formatDeliveryPlain(order) {
  const disp = getOrderDeliveryDisplay(order)
  if (disp.kind === 'lines') return disp.lines.join(', ')
  return disp.value === 'N/A' ? '' : String(disp.value || '')
}

function csvEscape(value) {
  const s = value == null ? '' : String(value)
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`
  return s
}

function formatIsoDate(value) {
  const date = parseInStockDate(value)
  if (!date) return ''
  return date.toISOString()
}

function buildOrdersCsv(orders) {
  const headers = [
    'Order ID',
    'Guest Arrival Date',
    'Property',
    'Property Address',
    'Delivery Address',
    'Commission',
  ]
  const rows = (orders || []).map((order) => {
    return [
      order.uniqueId || order._id || '',
      formatIsoDate(order.inStockDate),
      order.property?.listingTitle || '',
      formatAddressPlain(order.property?.address),
      formatDeliveryPlain(order),
      Number(order.hostFee || 0).toFixed(2),
    ].map(csvEscape).join(',')
  })
  return [headers.join(','), ...rows].join('\n')
}

function downloadOrdersCsv(orders, period) {
  const csv = buildOrdersCsv(orders)
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  const stamp = new Date().toISOString().slice(0, 10)
  a.href = url
  a.download = `orders-${period || 'allTime'}-${stamp}.csv`
  a.click()
  URL.revokeObjectURL(url)
}

function getStatusPillClasses(status) {
  switch (status?.toLowerCase()) {
    case 'completed': return 'bg-[#ECFDF5] border border-[#D1FAE5] text-[#047857]';
    case 'picked': return 'bg-blue-50 border border-blue-100 text-blue-700';
    case 'pending': case 'requested': case 'paymentcompleted': return 'bg-amber-50 border border-amber-100 text-amber-700';
    case 'cancelled': case 'canceled': return 'bg-red-50 border border-red-100 text-red-600';
    default: return 'bg-[#F5F5F5] border border-[#EAEAEA] text-[#5F6368]';
  }
}

function getStatusDisplayLabel(order) {
  if (isStatus(order.status, 'picked')) return 'Picked Up'
  if (isStatus(order.status, 'paymentCompleted')) return 'Payment Completed'
  return order.status || 'Unknown'
}

function formatShortDate(dateString) {
  if (!dateString) return '—'
  const date = new Date(dateString)
  if (Number.isNaN(date.getTime())) return '—'
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

function OrderTableRow({ order, index }) {
  const navigate = useNavigate()
  const canManageEntryInstructions = isEntryInstructionsEditable(order);

  return (
    <tr className="hover:bg-[#FCFAF7] transition-colors">
      <td className="px-4 py-3 whitespace-nowrap">
        <span className="font-bold text-[#111111]">
          #{order.uniqueId || order._id?.slice(-8) || `ORD-${index + 1}`}
        </span>
      </td>
      <td className="px-4 py-3">
        <p className="font-semibold text-[#111111] truncate max-w-[200px]">
          {order.property?.listingTitle || 'Property'}
        </p>
        <p className="text-[11px] text-[#888888] truncate max-w-[200px]">
          {order.property?.address?.city || '—'}
        </p>
      </td>
      <td className="px-4 py-3 whitespace-nowrap text-[#5F6368]">
        {formatShortDate(order.inStockDate)}
      </td>
      <td className="px-4 py-3 whitespace-nowrap text-[#5F6368]">
        {formatShortDate(order.createdAt)}
      </td>
      <td className="px-4 py-3 whitespace-nowrap">
        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-bold ${getStatusPillClasses(order.status)}`}>
          {getStatusDisplayLabel(order)}
        </span>
      </td>
      <td className="px-4 py-3 whitespace-nowrap">
        <span className="font-bold text-[#047857] tabular-nums">${order.hostFee?.toFixed(2) || '0.00'}</span>
      </td>
      <td className="px-4 py-3 text-right whitespace-nowrap text-[#888888]">
        {order.items?.length || 0}
      </td>
      <td className="px-4 py-3 text-right whitespace-nowrap">
        {canManageEntryInstructions ? (
          <button
            type="button"
            onClick={() => navigate(`/order-details/${order._id}`)}
            className="inline-flex items-center rounded-[10px] bg-[#111111] px-3 py-1.5 text-[12px] font-bold text-white transition hover:bg-black"
          >
            {order.hostOrderDetailsInputted ? 'Edit access' : 'Add access'}
          </button>
        ) : (
          <span className="text-[11px] font-semibold uppercase tracking-wide text-[#BBBBBB]">Closed</span>
        )}
      </td>
    </tr>
  );
}

function OrderCard({ order, index }) {
  const navigate = useNavigate()

  const formatAddress = (address) => {
    const plain = formatAddressPlain(address)
    return plain || 'N/A'
  };

  const deliveryDisplay = getOrderDeliveryDisplay(order);
  const canManageEntryInstructions = isEntryInstructionsEditable(order);

  return (
    <div className="rounded-[18px] bg-white border border-[#EAEAEA] overflow-hidden hover:border-[#111111] transition-colors">
      <div className="px-4 py-3 border-b border-[#F0F0F0] flex items-center justify-between gap-2">
        <div className="min-w-0">
          <h3 className="text-[14px] font-extrabold text-[#111111] truncate">
            #{order.uniqueId || order._id?.slice(-8) || `ORD-${index + 1}`}
          </h3>
          <p className="text-[11px] text-[#888888]">
            Arrival {formatShortDate(order.inStockDate)}
          </p>
        </div>
        <span className={`inline-flex shrink-0 items-center px-2 py-0.5 rounded-full text-[11px] font-bold ${getStatusPillClasses(order.status)}`}>
          {getStatusDisplayLabel(order)}
        </span>
      </div>

      <div className="p-4 space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
          <div className="bg-[#FAFAFA] rounded-[12px] p-3">
            <p className="text-[9px] font-bold uppercase tracking-[0.18em] text-[#888888] mb-1">Property</p>
            <p className="font-semibold text-[#111111] text-[12px]">{order.property?.listingTitle || 'Property'}</p>
            <p className="text-[11px] text-[#888888] mt-0.5">{formatAddress(order.property?.address)}</p>
          </div>
          <div className="bg-[#FAFAFA] rounded-[12px] p-3">
            <p className="text-[9px] font-bold uppercase tracking-[0.18em] text-[#888888] mb-1">Delivery Address</p>
            {deliveryDisplay.kind === 'lines' ? (
              deliveryDisplay.lines.map((line, i) => (
                <p key={i} className="text-[11px] text-[#111111] break-words">{line}</p>
              ))
            ) : (
              <p className="text-[11px] text-[#111111] break-words">{deliveryDisplay.value}</p>
            )}
          </div>
          <div className="bg-[#FAFAFA] rounded-[12px] p-3">
            <p className="text-[9px] font-bold uppercase tracking-[0.18em] text-[#888888] mb-1">Expected Delivery</p>
            <p className="text-[11px] text-[#111111]">{formatExpectedDeliveryPacific(order.inStockDate)}</p>
          </div>
          <div className="bg-[#ECFDF5] border border-[#D1FAE5] rounded-[12px] p-3">
            <p className="text-[9px] font-bold uppercase tracking-[0.18em] text-[#047857] mb-1">Commission Earned</p>
            <p className="text-[18px] font-extrabold text-[#047857] tabular-nums">${order.hostFee?.toFixed(2) || '0.00'}</p>
          </div>
        </div>

        {order.items && order.items.length > 0 && (
          <div>
            <p className="text-[9px] font-bold uppercase tracking-[0.18em] text-[#888888] mb-1.5">
              Items ({order.items.length})
            </p>
            <div className="divide-y divide-[#F0F0F0] rounded-[12px] border border-[#F0F0F0]">
              {order.items.slice(0, 3).map((item, itemIndex) => (
                <div key={itemIndex} className="flex items-center justify-between px-3 py-2">
                  <p className="text-[12px] text-[#111111]">
                    {item.name || 'Item'} <span className="text-[#888888]">× {item.quantity || 1}</span>
                  </p>
                  <p className="text-[12px] font-bold text-[#111111] tabular-nums">${item.price?.toFixed(2) || '0.00'}</p>
                </div>
              ))}
              {order.items.length > 3 && (
                <p className="text-[11px] text-[#888888] text-center py-1.5">+{order.items.length - 3} more</p>
              )}
            </div>
          </div>
        )}

        <div className="flex justify-end">
          {canManageEntryInstructions ? (
            <button
              type="button"
              onClick={() => navigate(`/order-details/${order._id}`)}
              className="inline-flex items-center rounded-[10px] bg-[#111111] px-3.5 py-2 text-[12px] font-bold text-white transition hover:bg-black"
            >
              {order.hostOrderDetailsInputted ? 'Edit entry instructions' : 'Add entry instructions'}
            </button>
          ) : (
            <span className="text-[11px] font-semibold uppercase tracking-wide text-[#BBBBBB]">
              Order closed
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

const STATUS_OPTIONS = [
  { value: 'all', label: 'All statuses' },
  { value: 'upcoming', label: 'Upcoming' },
  { value: 'pending', label: 'Pending' },
  { value: 'requested', label: 'Requested' },
  { value: 'completed', label: 'Completed' },
  { value: 'picked', label: 'Picked Up' },
  { value: 'cancelled', label: 'Cancelled' },
]

function OrderFilters({
  search,
  setSearch,
  statusFilter,
  setStatusFilter,
  viewMode,
  setViewMode,
  orders,
  arrivalPeriod,
  setArrivalPeriod,
  customFrom,
  setCustomFrom,
  customTo,
  setCustomTo,
  onExportCsv,
  exportCount,
}) {
  const getStatusCount = (status) => {
    if (!orders) return 0;
    if (status === 'all') return orders.length;
    if (status === 'upcoming') return orders.filter((o) => isUpcomingOrder(o)).length;
    return orders.filter((o) => isStatus(o.status, status)).length;
  };

  const hasActiveFilters = search || statusFilter !== 'all' || arrivalPeriod !== 'allTime' || customFrom || customTo;

  const resetFilters = () => {
    setSearch('');
    setStatusFilter('all');
    setArrivalPeriod('allTime');
    setCustomFrom('');
    setCustomTo('');
  };

  return (
    <div className="flex flex-wrap items-center gap-2 rounded-[14px] bg-white border border-[#EAEAEA] p-3 mb-4">
      {/* Guest Arrival segmented pills */}
      <span className="text-[9px] font-bold uppercase tracking-[0.18em] text-[#888888]">Arrival</span>
      <div className="flex items-center rounded-[10px] bg-[#F5F5F5] p-0.5" title="Filter by Guest Arrival date">
        {ARRIVAL_PERIOD_OPTIONS.map((option) => {
          const active = arrivalPeriod === option.value;
          return (
            <button
              key={option.value}
              type="button"
              onClick={() => setArrivalPeriod(option.value)}
              className={`h-8 rounded-[8px] px-2.5 text-[12px] transition-colors ${
                active ? 'bg-white shadow-sm font-bold text-[#111111]' : 'font-medium text-[#5F6368] hover:text-[#111111]'
              }`}
            >
              {option.label}
            </button>
          );
        })}
      </div>

      {arrivalPeriod === 'custom' && (
        <>
          <input
            type="date"
            value={customFrom}
            onChange={(e) => setCustomFrom(e.target.value)}
            aria-label="Guest arrival from"
            className="h-9 rounded-[10px] border border-[#EAEAEA] bg-white px-2.5 text-[12px] text-[#111111] focus:outline-none focus:border-[#111111]"
          />
          <span className="text-[11px] text-[#888888]">to</span>
          <input
            type="date"
            value={customTo}
            min={customFrom || undefined}
            onChange={(e) => setCustomTo(e.target.value)}
            aria-label="Guest arrival to"
            className="h-9 rounded-[10px] border border-[#EAEAEA] bg-white px-2.5 text-[12px] text-[#111111] focus:outline-none focus:border-[#111111]"
          />
        </>
      )}

      {/* Status dropdown with counts */}
      <span className="ml-1 text-[9px] font-bold uppercase tracking-[0.18em] text-[#888888]">Status</span>
      <select
        value={statusFilter}
        onChange={(e) => setStatusFilter(e.target.value)}
        aria-label="Filter by status"
        className="h-9 rounded-[10px] border border-[#EAEAEA] bg-white px-2.5 text-[13px] font-medium text-[#111111] focus:outline-none focus:border-[#111111]"
      >
        {STATUS_OPTIONS.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label} ({getStatusCount(option.value)})
          </option>
        ))}
      </select>

      {/* Search */}
      <div className="flex items-center flex-1 min-w-[180px] h-9 rounded-[10px] border border-[#EAEAEA] bg-[#FAFAFA] px-2.5">
        <svg className="h-3.5 w-3.5 text-[#888888] mr-2 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
        <input
          type="text"
          placeholder="Search ID, property, city..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="flex-1 min-w-0 bg-transparent outline-none text-[13px] text-[#111111] placeholder-[#999999]"
        />
        {search && (
          <button onClick={() => setSearch('')} className="text-[#888888] hover:text-[#111111] shrink-0" aria-label="Clear search">
            <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      {/* View toggle: icon-only */}
      <div className="flex items-center rounded-[10px] bg-[#F5F5F5] p-0.5">
        <button
          type="button"
          onClick={() => setViewMode('table')}
          title="Table view"
          aria-label="Table view"
          className={`flex items-center justify-center h-8 w-8 rounded-[8px] transition-colors ${viewMode === 'table' ? 'bg-white shadow-sm text-[#111111]' : 'text-[#888888] hover:text-[#111111]'}`}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" /></svg>
        </button>
        <button
          type="button"
          onClick={() => setViewMode('card')}
          title="Card view"
          aria-label="Card view"
          className={`flex items-center justify-center h-8 w-8 rounded-[8px] transition-colors ${viewMode === 'card' ? 'bg-white shadow-sm text-[#111111]' : 'text-[#888888] hover:text-[#111111]'}`}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" /></svg>
        </button>
      </div>

      {/* Export CSV */}
      <button
        type="button"
        onClick={onExportCsv}
        disabled={!exportCount}
        className="inline-flex items-center gap-1.5 h-9 px-3 rounded-[10px] bg-[#111111] text-white text-[12px] font-bold transition hover:bg-black disabled:opacity-40 disabled:cursor-not-allowed"
      >
        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5m0 0l5-5m-5 5V4" />
        </svg>
        Export CSV ({exportCount})
      </button>

      {/* Reset */}
      {hasActiveFilters && (
        <button
          type="button"
          onClick={resetFilters}
          className="inline-flex items-center gap-1.5 h-9 px-3 rounded-[10px] border border-[#EAEAEA] bg-white text-[12px] font-bold text-[#5F6368] transition hover:bg-[#FAFAFA] hover:text-[#111111]"
        >
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
          Reset
        </button>
      )}
    </div>
  );
}

function OrderStats({ orders }) {
  const earnedOrders = orders?.filter((o) => isStatus(o.status, 'completed') || isStatus(o.status, 'paymentCompleted')) || [];

  const stats = {
    total: orders?.length || 0,
    completed: orders?.filter((o) => isStatus(o.status, 'completed')).length || 0,
    upcoming: orders?.filter((o) => isUpcomingOrder(o)).length || 0,
    totalEarnings: earnedOrders.reduce((sum, order) => sum + (Number(order.hostFee) || 0), 0)
  };

  const statCards = [
    { label: 'Total Orders', value: stats.total },
    { label: 'Completed', value: stats.completed },
    { label: 'Upcoming', value: stats.upcoming },
    { label: 'Total Earnings', value: `$${stats.totalEarnings.toFixed(2)}` },
  ];

  return (
    <div className="grid grid-cols-2 laptop:grid-cols-4 gap-2.5 mb-4">
      {statCards.map((stat, i) => (
        <div key={i} className="rounded-[14px] bg-white border border-[#EAEAEA] px-4 py-3.5">
          <p className="text-[9px] font-bold uppercase tracking-[0.18em] text-[#888888]">{stat.label}</p>
          <p className="mt-1 text-[20px] font-extrabold leading-tight tabular-nums text-[#111111]" style={{ letterSpacing: -0.3 }}>
            {stat.value}
          </p>
        </div>
      ))}
    </div>
  );
}

function Pagination({ currentPage, totalPages, totalItems, pageSize, onPageChange, onPageSizeChange }) {
  const pageStart = (currentPage - 1) * pageSize + 1;
  const pageEnd = Math.min(currentPage * pageSize, totalItems);

  const getVisiblePages = () => {
    const pages = [];
    const maxVisible = 5;
    let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let end = Math.min(totalPages, start + maxVisible - 1);
    if (end - start + 1 < maxVisible) start = Math.max(1, end - maxVisible + 1);
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  };

  if (totalItems === 0) return null;

  return (
    <div className="flex flex-col sm:flex-row items-center justify-between gap-3 mt-4 px-1">
      <div className="flex items-center gap-3 text-[13px] text-[#5F6368]">
        <p>
          <span className="font-bold text-[#111111]">{pageStart}</span>–<span className="font-bold text-[#111111]">{pageEnd}</span> of <span className="font-bold text-[#111111]">{totalItems}</span>
        </p>
        <select
          value={pageSize}
          onChange={(e) => onPageSizeChange(Number(e.target.value))}
          aria-label="Rows per page"
          className="h-8 px-2 rounded-[8px] border border-[#EAEAEA] bg-white text-[12px] focus:outline-none focus:border-[#111111]"
        >
          {[10, 25, 50].map((size) => (<option key={size} value={size}>{size} / page</option>))}
        </select>
      </div>

      <div className="flex items-center gap-1">
        <button onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 1} aria-label="Previous page" className="flex items-center justify-center h-8 w-8 rounded-[8px] text-[#5F6368] hover:bg-white hover:text-[#111111] disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
        </button>
        {getVisiblePages().map((page) => (
          <button key={page} onClick={() => onPageChange(page)} className={`h-8 w-8 rounded-[8px] text-[13px] font-bold transition-colors ${page === currentPage ? 'bg-[#111111] text-white' : 'text-[#5F6368] hover:bg-white hover:text-[#111111]'}`}>
            {page}
          </button>
        ))}
        <button onClick={() => onPageChange(currentPage + 1)} disabled={currentPage === totalPages} aria-label="Next page" className="flex items-center justify-center h-8 w-8 rounded-[8px] text-[#5F6368] hover:bg-white hover:text-[#111111] disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
        </button>
      </div>
    </div>
  );
}

const OrdersScreen = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()

  const { loading, orders, error } = useSelector((state) => state.orders)
  const { hostInfo: { _id: hid } } = useSelector((state) => state.host)

  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [arrivalPeriod, setArrivalPeriod] = useState('allTime')
  const [customFrom, setCustomFrom] = useState('')
  const [customTo, setCustomTo] = useState('')
  const [viewMode, setViewMode] = useState('table')
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)

  const finalizedOrders = useMemo(() => (orders || []).filter((order) => isFinalizedOrder(order)), [orders])

  const arrivalFilteredOrders = useMemo(() => {
    return finalizedOrders.filter((order) =>
      orderMatchesArrivalPeriod(order, arrivalPeriod, customFrom, customTo)
    )
  }, [finalizedOrders, arrivalPeriod, customFrom, customTo])

  const filteredOrders = useMemo(() => {
    return arrivalFilteredOrders?.filter((order) => {
      const matchesSearch = !search ||
        (order.uniqueId && order.uniqueId.toLowerCase().includes(search.toLowerCase())) ||
        (order._id && order._id.toLowerCase().includes(search.toLowerCase())) ||
        (order.status && order.status.toLowerCase().includes(search.toLowerCase())) ||
        (order.property?.address?.line1 && order.property.address.line1.toLowerCase().includes(search.toLowerCase())) ||
        (order.property?.address?.city && order.property.address.city.toLowerCase().includes(search.toLowerCase()))

      const matchesStatus =
        statusFilter === 'all' ||
        (statusFilter === 'upcoming' && isUpcomingOrder(order)) ||
        isStatus(order.status, statusFilter)
      return matchesSearch && matchesStatus
    }) || []
  }, [arrivalFilteredOrders, search, statusFilter])

  const totalPages = Math.ceil(filteredOrders.length / pageSize)
  const paginatedOrders = useMemo(() => {
    const start = (currentPage - 1) * pageSize
    return filteredOrders.slice(start, start + pageSize)
  }, [filteredOrders, currentPage, pageSize])

  useEffect(() => { setCurrentPage(1) }, [search, statusFilter, pageSize, arrivalPeriod, customFrom, customTo])

  useEffect(() => {
    if (hid) dispatch(getAllOrders(hid))
  }, [dispatch, hid])

  const handleExportCsv = () => {
    downloadOrdersCsv(filteredOrders, arrivalPeriod)
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center">
        <div className="text-center">
          <CircularProgress size={60} sx={{ color: '#111111' }} />
          <p className="mt-4 text-[13px] text-[#888888] font-semibold">Loading orders...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <div className="max-w-[1200px] mx-auto px-6 py-6">
        <HostNav hide={true} />

        <div className="mb-5 flex items-end justify-between gap-3">
          <div>
            <p className="text-[9px] font-bold uppercase tracking-[0.22em] text-[#888888] mb-1">Orders</p>
            <h1 className="text-[26px] font-extrabold text-[#111111] leading-tight" style={{ letterSpacing: -0.5 }}>
              Order Management
            </h1>
            <p className="text-[13px] text-[#5F6368] mt-0.5">Track orders, filter by guest arrival, and export reports</p>
          </div>
          <Link
            to="/host"
            className="inline-flex shrink-0 items-center gap-1.5 h-9 px-3 rounded-[10px] border border-[#EAEAEA] bg-white text-[12px] font-bold text-[#5F6368] transition hover:bg-white hover:text-[#111111] hover:border-[#111111]"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
            Dashboard
          </Link>
        </div>

        <OrderStats orders={arrivalFilteredOrders} />
        <OrderFilters
          search={search}
          setSearch={setSearch}
          statusFilter={statusFilter}
          setStatusFilter={setStatusFilter}
          viewMode={viewMode}
          setViewMode={setViewMode}
          orders={arrivalFilteredOrders}
          arrivalPeriod={arrivalPeriod}
          setArrivalPeriod={setArrivalPeriod}
          customFrom={customFrom}
          setCustomFrom={setCustomFrom}
          customTo={customTo}
          setCustomTo={setCustomTo}
          onExportCsv={handleExportCsv}
          exportCount={filteredOrders.length}
        />

        {error && (
          <div className="bg-red-50 border border-red-100 rounded-[14px] px-4 py-3 mb-4">
            <p className="text-[13px] text-red-600 font-semibold">Error loading orders: {error}</p>
          </div>
        )}

        {filteredOrders.length === 0 ? (
          <div className="rounded-[18px] bg-white border border-[#EAEAEA] px-6 py-12 text-center">
            <h3 className="text-[16px] font-extrabold text-[#111111] mb-1">
              {search || statusFilter !== 'all' || arrivalPeriod !== 'allTime' ? 'No Orders Found' : 'No Orders Yet'}
            </h3>
            <p className="text-[13px] text-[#888888] mb-5">
              {search || statusFilter !== 'all' || arrivalPeriod !== 'allTime'
                ? 'Try adjusting your search or filter criteria'
                : 'Orders will appear here when guests make purchases'}
            </p>
            {(search || statusFilter !== 'all' || arrivalPeriod !== 'allTime') && (
              <button
                onClick={() => {
                  setSearch('')
                  setStatusFilter('all')
                  setArrivalPeriod('allTime')
                  setCustomFrom('')
                  setCustomTo('')
                }}
                className="inline-flex items-center h-9 px-4 rounded-[10px] bg-[#111111] text-white text-[12px] font-bold transition hover:bg-black"
              >
                Clear Filters
              </button>
            )}
          </div>
        ) : viewMode === 'table' ? (
          <div className="rounded-[18px] bg-white border border-[#EAEAEA] overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-[13px]">
                <thead className="bg-[#FCFAF7] text-[10px] uppercase tracking-[0.14em] text-[#888888]">
                  <tr>
                    <th className="px-4 py-2.5 font-bold whitespace-nowrap">Order</th>
                    <th className="px-4 py-2.5 font-bold whitespace-nowrap">Property</th>
                    <th className="px-4 py-2.5 font-bold whitespace-nowrap">Guest Arrival</th>
                    <th className="px-4 py-2.5 font-bold whitespace-nowrap">Ordered</th>
                    <th className="px-4 py-2.5 font-bold whitespace-nowrap">Status</th>
                    <th className="px-4 py-2.5 font-bold whitespace-nowrap">Commission</th>
                    <th className="px-4 py-2.5 font-bold whitespace-nowrap text-right">Items</th>
                    <th className="px-4 py-2.5 font-bold whitespace-nowrap text-right">Access</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {paginatedOrders.map((order, index) => (
                    <OrderTableRow key={order._id || index} order={order} index={(currentPage - 1) * pageSize + index} />
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-2.5">
            {paginatedOrders.map((order, index) => (
              <OrderCard key={order._id || index} order={order} index={(currentPage - 1) * pageSize + index} />
            ))}
          </div>
        )}

        <Pagination currentPage={currentPage} totalPages={totalPages} totalItems={filteredOrders.length} pageSize={pageSize} onPageChange={setCurrentPage} onPageSizeChange={setPageSize} />
      </div>
    </div>
  )
}

export default OrdersScreen
